/**
 * /sign/external/return — terminal-acknowledgement landing (route shell).
 *
 * LEGACY: this route was the post-AES landing back when /submit handed off to
 * an external AES provider (TrustFactory / Impression) that redirected here on
 * completion. The submit flow now applies an ECTA s13 signature on-server and
 * shows an in-place "Signed" confirmation, so the normal flow NO LONGER reaches
 * this route. It is kept (harmless) for any inbound provider/back-link that
 * still carries a `?status=...` and as a generic, status-keyed acknowledgement.
 *
 * `validateSearch` stays STATIC in this shell (it runs on navigation, before
 * the component renders). The terminal-acknowledgement UI lives in
 * `./return-screen.tsx` and is lazy-loaded — virtually no traffic lands here,
 * so it stays out of the initial bundle.
 *
 * Recognised `status` values (case-insensitive; aliases collapsed):
 *   success | signed | completed   → "Signature recorded"
 *   declined                       → "You declined to sign"
 *   cancelled | canceled           → "Signing cancelled"
 *   timeout | expired              → "Signing session timed out"
 *   error | failed | <anything>    → neutral "session ended — check with firm"
 */

import { createFileRoute, lazyRouteComponent } from '@tanstack/react-router';
import { normaliseStatus, type ReturnStatus } from './return-status';

interface ReturnSearch {
  status: ReturnStatus;
  /** Provider's own correlation / reference, surfaced for support. */
  ref?: string;
}

export const Route = createFileRoute('/sign/external/return')({
  component: lazyRouteComponent(() => import('./return-screen'), 'ExternalSignReturn'),
  validateSearch: (search: Record<string, unknown>): ReturnSearch => {
    const out: ReturnSearch = { status: normaliseStatus(search['status']) };
    // Accept a few common provider param names for a support reference.
    const ref = search['ref'] ?? search['reference'] ?? search['correlationId'] ?? search['transactionId'];
    if (typeof ref === 'string' && ref.length > 0 && ref.length <= 128) out.ref = ref;
    return out;
  },
});
